import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  CapaErrorCode,
  CapaQuotationChatMessageType,
  CapaQuotationChatRoomStatus,
  CapaQuotationPartnerStatus,
  useFetchWithdrawnQuotationPartnerQuery,
  useSendQuotationChatMessageMutation
} from '@generated/graphql';
import { useSelector } from '@store';
import ChatMessageInputPresenter from './ChatMessageInputPresenter';
import errorMessage from '@utils/errorMessage';

const disabledRoomState = [
  CapaQuotationChatRoomStatus.ClosedClient,
  CapaQuotationChatRoomStatus.ClosedPartner,
  CapaQuotationChatRoomStatus.ClosedConfirm,
  CapaQuotationChatRoomStatus.Stopped
];

interface Props {
  matches: boolean;
  loading: boolean;
  scrollToBottom: () => void;
  onOpen: () => void;
}

const ChatMessageInputContainer = ({
  matches,
  loading,
  scrollToBottom,
  onOpen
}: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const { chatRoomId } = useParams() as { chatRoomId: string };
  const partnerId = useSelector((state) => state.chat.quotationPartnerId);
  const roomStatus = useSelector((state) => state.chat.chatRoomStatus);

  const { data } = useFetchWithdrawnQuotationPartnerQuery({
    variables: {
      quotationPartnerId: partnerId ?? ''
    },
    fetchPolicy: 'cache-first',
    skip: !partnerId
  });

  const [sendMessageMutation] = useSendQuotationChatMessageMutation({
    onCompleted: (response) => {
      if (response.sendQuotationChatMessage.result === CapaErrorCode.Success) {
        scrollToBottom();
      } else {
        enqueueSnackbar(errorMessage.sendChatMessage);
      }
    },
    onError: () => {
      enqueueSnackbar(errorMessage.sendChatMessage);
    }
  });

  const partner = data?.fetchWithdrawnQuotationPartner.data ?? undefined;

  const isWithdraw = partner?.status === CapaQuotationPartnerStatus.Deleted;

  const isDisabled = roomStatus
    ? disabledRoomState.includes(roomStatus) || isWithdraw
    : false;

  const onSend = useCallback(
    async (message: string, isBypassSuspected: boolean) => {
      await sendMessageMutation({
        variables: {
          input: {
            message,
            chatRoomId: chatRoomId as string,
            messageType: isBypassSuspected
              ? CapaQuotationChatMessageType.BypassPrevention
              : undefined
          }
        }
      });
    },
    [sendMessageMutation, chatRoomId]
  );

  return (
    <ChatMessageInputPresenter
      loading={loading}
      matches={matches}
      isWithdraw={isWithdraw}
      isDisabled={isDisabled || loading}
      onSend={onSend}
      onOpenUploadBox={onOpen}
    />
  );
};

export default ChatMessageInputContainer;
